import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Chip,
} from "@mui/material";
import TextType from "../Components/common/TextTypeAnimation";

const HowItWorksSection = () => {
  const [skillInput, setSkillInput] = useState("");

  // Skills for the typing animation
  const skills = [
    "Search anything",
    "Photography",
    "Civil engineering",
    "Marketing",
    "Sales",
    "Accounting",
    "HR",
    "Legal",
    "Customer service",
    "Design",
    "Development",
    "Product management",
    "Driving",
    "Manager",
    "Tracking",
    "Writing",
    "Painting",
  ];

  return (
    <Box
      sx={{
        backgroundColor: "#000",
        py: { xs: 4, sm: 6, md: 8 },
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Container maxWidth="xl">
        {/* Section Title */}
        <Typography
          variant="h2"
          sx={{
            color: "#fff",
            fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem", lg: "3.5rem" },
            fontWeight: 700,
            textAlign: "center",
            mb: { xs: 4, md: 6 },
          }}
        >
          How It Works
        </Typography>

        {/* Descriptive Paragraph */}
        <Typography
          sx={{
            color: "#fff",
            fontFamily: "'SF Pro Display', 'Inter', sans-serif",
            fontWeight: 500, // matches Medium in Figma
            fontStyle: "normal",
            fontSize: {
              xs: "14px", // mobile
              sm: "16px", // small tablets
              md: "18px", // large tablets
              lg: "20px", // desktop (matches Figma)
              xl: "20px", // keep same size for ultra-wide
            },
            lineHeight: {
              xs: "22px",
              sm: "26px",
              md: "28px",
              lg: "30px", // matches Figma height proportion
            },
            textAlign: "center",
            mb: { xs: 2, sm: 3, md: 4 },
            maxWidth: {
              xs: "100%", // full width mobile
              sm: "90%",
              md: "900px",
              lg: "1284px", // your Figma width
            },
            mx: "auto",
            px: { xs: 2, sm: 3 },
            display: "flex",
            alignItems: "center", // vertically centers within its container height
            minHeight: {
              lg: "60px", // your Figma height
            },
          }}
        >
          Over 80% of professionals feel uncertain about their next step — even
          with years of experience. We transform your existing knowledge into a
          clear career pathway by mapping skills, highlighting industry
          opportunities, and providing data-backed career action plans.
        </Typography>

        <Box
          sx={{
            borderRadius: "20px",
            border: "1px solid #333",
            backgroundColor: "#111",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 4, sm: 5, md: 6 },
            minHeight: { xs: "auto", md: "600px" },
          }}
        >
          {/* Prompt Text */}
          <Typography
            sx={{
              fontFamily:
                "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              fontWeight: 600,
              fontStyle: "bold",
              fontSize: { xs: "24px", sm: "32px", md: "40px", lg: "44px" },
              lineHeight: { xs: "28px", sm: "38px", md: "48px", lg: "60px" },
              letterSpacing: "0%",
              textAlign: "left",
              verticalAlign: "middle",
              color: "#fff",
              mb: { xs: 4, sm: 5, md: 6 },
            }}
          >
            Enter any <span style={{ color: "#FFD700" }}>topic</span>,{" "}
            <span style={{ color: "#FFD700" }}>domain</span>,{" "}
            <span style={{ color: "#FFD700" }}>skill</span>,{" "}
            <span style={{ color: "#FFD700" }}>role</span>, or{" "}
            <span style={{ color: "#FFD700" }}>activity</span>
          </Typography>

          {/* Input Field with Button */}
          <Box
            sx={{
              width: "100%",
              position: "relative",
            }}
          >
            {/* Status Chips */}
            <Box
              sx={{
                display: "flex",
                gap: { xs: "8px", md: "14px" },
                flexWrap: "wrap",
                justifyContent: "flex-start",
                mb: { xs: 1, md: 1.5 },
                width: "100%",
              }}
            >
              <Chip
                label="Trending"
                sx={{
                  backgroundColor: "#4CAF50",
                  color: "#fff",
                  fontFamily: "'SF Pro Display', 'Inter', sans-serif",
                  fontWeight: 500,
                  fontStyle: "normal",
                  fontSize: { xs: "14px", sm: "16px", md: "18px" },
                  lineHeight: { xs: "18px", sm: "20px", md: "24px" },
                  padding: { xs: "4px 8px", md: "8px 16px" },
                  borderRadius: "20px",
                }}
              />
              <Chip
                label="In Demand"
                sx={{
                  backgroundColor: "#FF9800",
                  color: "#fff",
                  fontFamily: "'SF Pro Display', 'Inter', sans-serif",
                  fontWeight: 500,
                  fontStyle: "normal",
                  fontSize: { xs: "14px", sm: "16px", md: "18px" },
                  lineHeight: { xs: "18px", sm: "20px", md: "24px" },
                  padding: { xs: "4px 8px", md: "8px 16px" },
                  borderRadius: "20px",
                }}
              />
              <Chip
                label="Getting outdated fast"
                sx={{
                  backgroundColor: "#F44336",
                  color: "#fff",
                  fontFamily: "'SF Pro Display', 'Inter', sans-serif",
                  fontWeight: 500,
                  fontStyle: "normal",
                  fontSize: { xs: "14px", sm: "16px", md: "18px" },
                  lineHeight: { xs: "18px", sm: "20px", md: "24px" },
                  padding: { xs: "4px 8px", md: "8px 16px" },
                  borderRadius: "20px",
                }}
              />
              <Chip
                label="Obsolete"
                sx={{
                  backgroundColor: "#2196F3",
                  color: "#fff",
                  fontFamily: "'SF Pro Display', 'Inter', sans-serif",
                  fontWeight: 500,
                  fontStyle: "normal",
                  fontSize: { xs: "14px", sm: "16px", md: "18px" },
                  lineHeight: { xs: "18px", sm: "20px", md: "24px" },
                  padding: { xs: "4px 8px", md: "8px 16px" },
                  borderRadius: "20px",
                }}
              />
            </Box>
            <TextField
              fullWidth
              placeholder=""
              value={skillInput}
              onChange={(e) => setSkillInput(e.target.value)}
              disabled
              sx={{
                mb: { xs: 4, sm: 5, md: 6 },
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#1a1a1a",
                  borderRadius: "18px",
                  border: "2px solid #333",
                  color: "#fff",
                  height: { xs: "60px", sm: "80px", md: "100px" },
                  transition: "all 0.3s ease",
                  "& fieldset": {
                    borderColor: "transparent",
                    borderWidth: "0px",
                  },
                  "& input": {
                    color: "#fff",
                    fontSize: { xs: "20px", sm: "24px", md: "30px" },
                    height: { xs: "60px", sm: "80px", md: "100px" },
                    padding: { xs: "0 16px", md: "0 24px" },
                    fontWeight: 600,
                    fontFamily:
                      "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                    fontStyle: "bold",
                    lineHeight: { xs: "40px", sm: "48px", md: "60px" },
                    cursor: "default",
                    "-webkit-text-fill-color": "#fff",
                  },
                  "& input::placeholder": {
                    color: "transparent",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#1a1a1a",
                    "& fieldset": {
                      borderColor: "transparent",
                    },
                    "& input": {
                      color: "#fff",
                      "-webkit-text-fill-color": "#fff",
                    },
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <TextType
                    text={skills}
                    typingSpeed={75}
                    pauseDuration={1500}
                    showCursor={true}
                    cursorCharacter="|"
                    textColors={["#fff"]}
                    className=""
                  />
                ),
              }}
            />
          </Box>

          {/* Check Relevance Button */}
          <Button
            variant="contained"
            onClick={() => console.log("Check relevance clicked")}
            sx={{
              background: "linear-gradient(135deg, #6652E4 0%, #8B7CF6 100%)",
              color: "#fff",
              fontFamily:
                "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              fontWeight: 500,
              fontStyle: "normal",
              fontSize: { xs: "20px", sm: "24px", md: "30px" },
              lineHeight: { xs: "30px", sm: "32px", md: "40px" },
              textAlign: "center",
              padding: "0",
              borderRadius: { xs: "40px", md: "55px" },
              textTransform: "none",
              transition: "all 0.3s ease",
              width: { xs: "240px", sm: "280px", md: "325px" },
              height: { xs: "48px", sm: "56px", md: "64px" },
              "&:active": {
                transform: "translateY(-1px)",
              },
            }}
          >
            Check Relevance
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default HowItWorksSection;
